/**
 * ALBERO UI components: accordion, product rail (tabs + arrows), scroll reveals,
 * hero entrance, localization auto-submit and Theme Editor integration.
 */
import { config, prefersReducedMotion } from '@albero/core';

const EASE = 'cubic-bezier(.22,1,.36,1)';

/* -------------------------------------------------------------------------- */
/* Accordion                                                                   */
/* -------------------------------------------------------------------------- */

class AlberoAccordion extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('.albero-accordion__trigger');
      if (!trigger || !this.contains(trigger)) return;
      const item = trigger.closest('[data-accordion-item]');
      this.toggle(item, trigger.getAttribute('aria-expanded') !== 'true');
    });
  }

  toggle(item, open) {
    if (!item) return;
    if (open && this.hasAttribute('data-single')) {
      this.querySelectorAll('[data-accordion-item].is-open').forEach((other) => {
        if (other !== item) this.toggle(other, false);
      });
    }
    const trigger = item.querySelector('.albero-accordion__trigger');
    const panel = item.querySelector('.albero-accordion__panel');
    item.classList.toggle('is-open', open);
    trigger?.setAttribute('aria-expanded', String(open));
    panel?.toggleAttribute('inert', !open);
  }
}

if (!customElements.get('albero-accordion')) customElements.define('albero-accordion', AlberoAccordion);

/* -------------------------------------------------------------------------- */
/* Product rail: tabs + arrows                                                 */
/* -------------------------------------------------------------------------- */

class AlberoProductRail extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = this.tabs[(index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length];
        next.focus();
        this.select(next);
      });
    });
    this.querySelector('[data-rail-prev]')?.addEventListener('click', () => this.scroll(-1));
    this.querySelector('[data-rail-next]')?.addEventListener('click', () => this.scroll(1));
  }

  get activeTrack() {
    const panel = [...this.querySelectorAll('[data-rail-panel]')].find((el) => !el.hidden);
    return panel?.querySelector('[data-rail-track]');
  }

  select(tab) {
    this.tabs.forEach((other) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(other.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
    this.activeTrack?.scrollTo({ left: 0 });
    revealWithin(this);
  }

  scroll(direction) {
    const track = this.activeTrack;
    if (!track) return;
    track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('albero-product-rail')) customElements.define('albero-product-rail', AlberoProductRail);

/* -------------------------------------------------------------------------- */
/* Product story: sticky media that follows the active text step              */
/* -------------------------------------------------------------------------- */

class AlberoStory extends HTMLElement {
  connectedCallback() {
    this.steps = [...this.querySelectorAll('[data-story-step]')];
    this.frames = [...this.querySelectorAll('[data-story-frame]')];
    this.bars = [...this.querySelectorAll('[data-story-bar]')];
    if (!this.steps.length) return;
    this.observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.activate(Number(entry.target.dataset.storyStep));
        }),
      { rootMargin: '-45% 0px -45% 0px' }
    );
    this.steps.forEach((step) => this.observer.observe(step));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  activate(index) {
    [this.steps, this.frames, this.bars].forEach((list) =>
      list.forEach((el, i) => el.classList.toggle('is-active', i === index))
    );
    this.frames.forEach((frame, i) => {
      const video = frame.querySelector('video');
      if (!video) return;
      if (i === index && !prefersReducedMotion()) video.play?.().catch(() => {});
      else video.pause?.();
    });
  }
}

if (!customElements.get('albero-story')) customElements.define('albero-story', AlberoStory);

/* -------------------------------------------------------------------------- */
/* Reveal animations                                                           */
/* -------------------------------------------------------------------------- */

const animationsEnabled = () => config.animations !== false && !prefersReducedMotion() && 'animate' in Element.prototype;

let revealObserver = null;
function getObserver() {
  if (revealObserver) return revealObserver;
  revealObserver = new IntersectionObserver(
    (entries) =>
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        revealObserver.unobserve(el);
        playReveal(el);
      }),
    { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
  );
  return revealObserver;
}

function playReveal(el) {
  el.classList.remove('albero-reveal-pending');
  const scale = el.dataset.reveal === 'scale';
  el.animate(
    scale
      ? [
          { opacity: 0, transform: 'scale(.94)' },
          { opacity: 1, transform: 'scale(1)' },
        ]
      : [
          { opacity: 0, transform: 'translate3d(0,32px,0)' },
          { opacity: 1, transform: 'translate3d(0,0,0)' },
        ],
    { duration: scale ? 1100 : 800, delay: Number(el.dataset.revealDelay) || 0, easing: EASE, fill: 'backwards' }
  );
}

function revealWithin(root = document) {
  const elements = root.querySelectorAll('[data-reveal]:not([data-reveal-ready])');
  if (!animationsEnabled() || config.designMode) {
    elements.forEach((el) => el.setAttribute('data-reveal-ready', ''));
    return;
  }
  const observer = getObserver();
  elements.forEach((el) => {
    el.setAttribute('data-reveal-ready', '');
    // Content already on screen stays visible: only animate what's below the fold.
    if (el.getBoundingClientRect().top > window.innerHeight * 0.92) {
      el.classList.add('albero-reveal-pending');
      observer.observe(el);
    }
  });
}

function heroEntrance(root = document) {
  if (!animationsEnabled()) return;
  root.querySelectorAll('.albero-hero').forEach((hero) => {
    if (hero.dataset.entered) return;
    hero.dataset.entered = 'true';
    hero.querySelectorAll('[data-hero-item]').forEach((el, index) => {
      el.animate(
        [
          { opacity: 0, transform: 'translate3d(0,28px,0)' },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 900, delay: 80 + index * 95, easing: EASE, fill: 'backwards' }
      );
    });
    hero.querySelector('[data-hero-media]')?.animate(
      [
        { clipPath: 'inset(6% 6% 6% 6% round 32px)', opacity: 0 },
        { clipPath: 'inset(0% 0% 0% 0% round 0px)', opacity: 1 },
      ],
      { duration: 1300, delay: 120, easing: EASE, fill: 'backwards' }
    );
  });
}

function init(root = document) {
  heroEntrance(root);
  revealWithin(root);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
else init();

/* -------------------------------------------------------------------------- */
/* Localization selector: submit on change                                     */
/* -------------------------------------------------------------------------- */

document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-albero-localization] select');
  if (select) select.form?.submit();
});

/* -------------------------------------------------------------------------- */
/* Colour showcase → main picker                                               */
/* -------------------------------------------------------------------------- */

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-albero-select-option]');
  if (!trigger) return;
  event.preventDefault();
  document.dispatchEvent(
    new CustomEvent('albero:select-option', {
      detail: {
        productId: trigger.dataset.productId,
        optionName: trigger.dataset.optionName,
        value: trigger.dataset.alberoSelectOption,
      },
    })
  );
});

/* -------------------------------------------------------------------------- */
/* Theme Editor                                                                */
/* -------------------------------------------------------------------------- */

document.addEventListener('shopify:section:load', (event) => init(event.target));

document.addEventListener('shopify:block:select', (event) => {
  const block = event.target;
  // Open accordion rows when selected in the editor
  const item = block.closest?.('[data-accordion-item]') || block.querySelector?.('[data-accordion-item]');
  const accordion = item?.closest('albero-accordion');
  if (accordion && item) accordion.toggle(item, true);
  // Bring the selected tab into view
  if (block.getAttribute?.('role') === 'tab') block.closest('albero-product-rail')?.select(block);
  // Scroll horizontal tracks to the selected block
  const track = block.closest?.('.albero-scroller');
  if (track && track !== block) track.scrollTo({ left: block.offsetLeft - track.offsetLeft - 16, behavior: 'smooth' });
});